import React, { useContext, useState } from 'react';

import MapView, { MapLocation } from '../MapView';
import { ScheduleContext } from '../../contexts';
import DaySelection, { CourseDateItem, Day, isDay } from '../DaySelection';
import { Meeting } from '../../types';

import './stylesheet.scss';

// Construct combined course data
// to pass to `<MapView>` and `<DaySelection>`
type CombinedCourseData = CourseDateItem & MapLocation;

export default function Map(): React.ReactElement {
  const [{ oscar, pinnedCrns }] = useContext(ScheduleContext);
  const [activeDay, setActiveDay] = useState<Day | ''>('ALL');
  const courseDateMap: Record<Day, CombinedCourseData[]> = {
    ALL: [],
    M: [],
    T: [],
    W: [],
    R: [],
    F: [],
  };

  // Construct the course data for the first meeting of each class
  pinnedCrns.forEach((crn) => {
    const section = oscar.findSection(crn);
    if (section == null) return;
    const { meetings } = section;
    if (meetings.length === 0) return;
    const firstMeeting = meetings[0] as Meeting;

    firstMeeting.days.forEach((day) => {
      if (!isDay(day)) return;
      const courseItem: CombinedCourseData = {
        id: section.course.id,
        title: section.course.title,
        times: firstMeeting.period,
        daysOfWeek: firstMeeting.days,
        section: section.id,
        coords: firstMeeting.location,
      };
      courseDateMap[day].push(courseItem);
    });
  });

  // Sort each list of course data by their times
  const sortedCourseDateMap: Record<Day, CombinedCourseData[]> = {
    ALL: [],
    M: [],
    T: [],
    W: [],
    R: [],
    F: [],
  };
  // Dedupe by course *and* section so that separate sections of the same
  // course (e.g. a lecture A1 and its lab A04) both appear under "All Days",
  // while a single section meeting on several days is only listed once.
  const seenSections = new Set<string>();
  Object.entries(courseDateMap).forEach(([day, courseDataList]) => {
    if (!isDay(day) || day === 'ALL') return;
    sortedCourseDateMap[day] = courseDataList.sort(
      (a, b) => (a.times?.start ?? 0) - (b.times?.start ?? 0)
    );
    sortedCourseDateMap[day].forEach((course) => {
      const sectionKey = `${course.id}-${course.section}`;
      if (!seenSections.has(sectionKey)) {
        seenSections.add(sectionKey);
        sortedCourseDateMap.ALL.push({ ...course });
      }
    });
  });

  // Keep "All Days" ordered by start time across the whole week
  sortedCourseDateMap.ALL.sort(
    (a, b) => (a.times?.start ?? 0) - (b.times?.start ?? 0)
  );

  let activeLocations: MapLocation[] = [];
  if (activeDay !== '') {
    activeLocations = sortedCourseDateMap[activeDay];
  }

  return (
    <div className="map-content">
      <DaySelection
        courseDateMap={sortedCourseDateMap}
        activeDay={activeDay}
        setActiveDay={setActiveDay}
      />
      <MapView locations={activeLocations} />
    </div>
  );
}
